import { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { ApplyToAll, Copy, Success, WithTooltip } from 'basic-components';
import { CommonService } from 'dashboard-services/services';
import { globalActions } from 'primary-components';

import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/theme-textmate';
import 'ace-builds/src-noconflict/theme-idle_fingers';

import './CodeDiff.scss';

const EDITOR_PROPS = { $blockScrolling: true }

const getLcs = (beforeLines, afterLines) => {
  const matrix = Array.from({ length: beforeLines.length + 1 }, () => Array(afterLines.length + 1).fill(0))

  beforeLines
    .map((_, row) => row)
    .reverse()
    .forEach(row => {
      afterLines
        .map((_, col) => col)
        .reverse()
        .forEach(col => {
          matrix[row][col] = beforeLines[row] === afterLines[col]
            ? matrix[row + 1][col + 1] + 1
            : Math.max(matrix[row + 1][col], matrix[row][col + 1])
        })
    })

  return matrix
}

const addDiffRow = ({ lineNumber, rows, type, value }) => rows.push({ lineNumber, type, value })

const pushRemainingRows = ({ fromIndex, lines, rows, type }) =>
  lines.slice(fromIndex).forEach((value, index) => addDiffRow({
    lineNumber: fromIndex + index + 1,
    rows,
    type,
    value
  }))

const REMOVED = "removed", ADDED = "added";
const getDiffRows = ({ beforeCode = "", afterCode = "" }) => {
  const beforeLines = String(beforeCode).split("\n"),
        afterLines = String(afterCode).split("\n"),
        lcs = getLcs(beforeLines, afterLines),
        rows = []

  const walkDiff = (beforeIndex = 0, afterIndex = 0) => {
    if(beforeIndex >= beforeLines.length || afterIndex >= afterLines.length) {
      pushRemainingRows({ fromIndex: beforeIndex, lines: beforeLines, rows, type: REMOVED })
      pushRemainingRows({ fromIndex: afterIndex, lines: afterLines, rows, type: ADDED })
      return;
    }
    if(beforeLines[beforeIndex] === afterLines[afterIndex]) {
      walkDiff(beforeIndex + 1, afterIndex + 1)
      return;
    }
    if(lcs[beforeIndex + 1][afterIndex] >= lcs[beforeIndex][afterIndex + 1]) {
      addDiffRow({ lineNumber: beforeIndex + 1, rows, type: REMOVED, value: beforeLines[beforeIndex] })
      walkDiff(beforeIndex + 1, afterIndex)
      return;
    }
    addDiffRow({ lineNumber: afterIndex + 1, rows, type: ADDED, value: afterLines[afterIndex] })
    walkDiff(beforeIndex, afterIndex + 1)
  }

  walkDiff()

  return rows
}

const CodeDiff = ({ afterCode, beforeCode, onApply }) => {
  const dispatch = useDispatch(),
        [isApplied, setIsApplied] = useState(false),
        diffRows = useMemo(() => getDiffRows({ afterCode, beforeCode }), [afterCode, beforeCode]),
        diffValue = useMemo(() => diffRows.map(row => `${String(row.lineNumber).padStart(4, " ")} ${row.type === ADDED ? "+" : "-"} ${row.value || " "}`).join("\n"), [diffRows]),
        markers = useMemo(() => diffRows.map((row, index) => ({
          className: `ng-python-editor__chat__content__chat__row__diff__ace-line--${row.type}`,
          endCol: 1,
          endRow: index,
          startCol: 0,
          startRow: index,
          type: 'fullLine'
        })), [diffRows]),
        onLoad = useCallback(editor => {
          editor.renderer.setShowGutter(false)
          editor.setHighlightActiveLine(false)
          editor.setReadOnly(true)
          editor.setShowPrintMargin(false)
          editor.setOption("displayIndentGuides", false)
          editor.setOption("highlightGutterLine", false)
          editor.setOption("useWorker", false)
        }, []),
        onCopy = useCallback(() => {
          CommonService.copyToClipboard(afterCode)
          dispatch(globalActions.getMessageStore()).success("Generated code copied to clipboard")
        }, [afterCode, dispatch]),
        onApplyLocal = useCallback(() => {
          onApply(afterCode)
          setIsApplied(true)
          dispatch(globalActions.getMessageStore()).success("Code applied to editor")
        }, [afterCode, dispatch, onApply])

  if(diffRows.length === 0) {
    return undefined;
  }

  return (
    <div className="ng-python-editor__chat__content__chat__row__diff">
      <div className="ng-python-editor__chat__content__chat__row__diff__header">
        <div className="ng-python-editor__chat__content__chat__row__diff__language">Python</div>
        <div className="ng-python-editor__chat__content__chat__row__diff__actions">
          <WithTooltip text="Copy code">
            <div
                className="ng-python-editor__chat__content__chat__row__diff__action"
                onClick={onCopy}
            >
              <Copy color="blue-bright"/>
            </div>
          </WithTooltip>
          <WithTooltip text="Apply code to editor">
            <div
                className="ng-python-editor__chat__content__chat__row__diff__action"
                onClick={onApplyLocal}
            >
              <ApplyToAll color="blue-bright"/>
            </div>
          </WithTooltip>
        </div>
      </div>
      <div className="ng-python-editor__chat__content__chat__row__diff__content">
        <AceEditor
            editorProps={EDITOR_PROPS}
            fontSize={10}
            height={`${Math.min(diffRows.length * 14, 240)}px`}
            highlightActiveLine={false}
            lineHeight={14}
            markers={markers}
            mode="python"
            name="PythonChatDiff"
            onLoad={onLoad}
            readOnly
            showGutter={false}
            tabSize={2}
            theme="textmate"
            value={diffValue}
            width="100%"
        />
      </div>
      {isApplied ? (
        <div className="ng-python-editor__chat__content__chat__row__diff__note">
          <Success 
              bgColor="green-fresh"
              color="white-base" 
              height={8} 
              width={8}
          />
          <p style={{ display: "inline-block", marginLeft: '4px' }}>Code applied!</p>
        </div>
      ) : undefined}
    </div>
  )
}

CodeDiff.propTypes = {
  afterCode: PropTypes.string,
  beforeCode: PropTypes.string,
  onApply: PropTypes.func
}

export default memo(CodeDiff);
